import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import "../styles/home.scss";
import axios from "axios";
import { backend } from "../../../components/apipath";

// CMA 目前兩個 cate
const CMA_CATE_IDS = [76, 83];
// 學分班 cate
const SCE_CATE_ID = 72;

// 將各種可能的回傳格式轉成扁平陣列
function toFlatArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === "object") {
    return Object.values(payload).flat();
  }
  return [];
}

// 從 title 解析年份（例如： "CPA 學員分享 (2024)" -> 2024）
function parseYearFromTitle(title) {
  const m = /\((\d{4})\)/.exec(title || "");
  return m ? parseInt(m[1], 10) : null;
}

export default function Sharearea() {
  // 'CPA' | 'CMA' | 'SCE'
  const [activeGroup, setActiveGroup] = useState("CPA");

  // /home/shareCategories 回來的清單（至少含 {cate_id, title}）
  const [categories, setCategories] = useState([]);
  // CPA 近五年 [{year, cate_id, title}]
  const [cpaYears, setCpaYears] = useState([]);
  // 目前 CPA 選擇的 cate_id
  const [activeCateId, setActiveCateId] = useState(null);

  const [items, setItems] = useState([]); // 當前顯示的文章（CPA: 單年；CMA/SCE: 合併）
  const [loading, setLoading] = useState(false);

  // 前端分頁
  const [currentPage, setCurrentPage] = useState(1);
  const cardsPerPage = 8;
  const totalPages = Math.ceil(items.length / cardsPerPage);
  const currentCards = useMemo(() => {
    const start = (currentPage - 1) * cardsPerPage;
    return items.slice(start, start + cardsPerPage);
  }, [items, currentPage]);

  useEffect(() => {
    // 取得 cate 清單後，計算 CPA 近五年清單，並預設載入 CPA 最新一年
    fetchCategories()
      .then((list) => {
        setCategories(list);
        const cpaYearCates = list
          .filter((c) => /CPA/.test(c.title) && /\(\d{4}\)/.test(c.title))
          .map((c) => ({
            year: parseYearFromTitle(c.title),
            cate_id: c.cate_id,
            title: c.title,
          }))
          .filter((c) => !!c.year)
          .sort((a, b) => b.year - a.year);

        const top5 = cpaYearCates.slice(0, 5);
        setCpaYears(top5);

        if (top5.length) {
          setActiveGroup("CPA");
          setActiveCateId(top5[0].cate_id);
          loadCPA(top5[0].cate_id);
        } else {
          // 沒年份就先顯示學分班
          setActiveGroup("SCE");
          loadSCE();
        }
      })
      .catch((err) => {
        console.error(err);
        // 失敗時保底顯示學分班
        setActiveGroup("SCE");
        loadSCE();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- 資料載入方法 ---
  async function fetchCategories() {
    // 期望後端提供：/home/shareCategories -> [{ cate_id, title }, ...]
    const { data } = await axios.get(`${backend}home/shareCategories`);
    // 也可改成硬編寫死：回傳你那串 cate_id + title 的陣列
    return Array.isArray(data) ? data : [];
  }

  async function loadCPA(cateId) {
    try {
      setLoading(true);
      setCurrentPage(1);
      const { data } = await axios.get(
        `${backend}home/selectShareByYear/${cateId}`
      );
      const flat = toFlatArray(data);
      // 有 create_date 就用日期排，否則用 art_id 排
      flat.sort(
        (a, b) =>
          new Date(b.create_date || 0) - new Date(a.create_date || 0) ||
          (b.art_id || 0) - (a.art_id || 0)
      );
      setItems(flat);
    } catch (e) {
      console.error(e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  async function loadCMA() {
    try {
      setLoading(true);
      setCurrentPage(1);
      const reqs = CMA_CATE_IDS.map((id) =>
        axios
          .get(`${backend}home/selectShareByYear/${id}`)
          .catch(() => ({ data: [] }))
      );
      const results = await Promise.all(reqs);
      const merged = results.flatMap((r) => toFlatArray(r.data));
      merged.sort(
        (a, b) =>
          new Date(b.create_date || 0) - new Date(a.create_date || 0) ||
          (b.art_id || 0) - (a.art_id || 0)
      );
      setItems(merged);
    } catch (e) {
      console.error(e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  async function loadSCE() {
    try {
      setLoading(true);
      setCurrentPage(1);
      const { data } = await axios.get(
        `${backend}home/selectShareByYear/${SCE_CATE_ID}`
      );
      const flat = toFlatArray(data);
      flat.sort(
        (a, b) =>
          new Date(b.create_date || 0) - new Date(a.create_date || 0) ||
          (b.art_id || 0) - (a.art_id || 0)
      );
      setItems(flat);
    } catch (e) {
      console.error(e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  // --- 切換邏輯 ---
  function switchToCPA(cateId) {
    setActiveGroup("CPA");
    setActiveCateId(cateId);
    loadCPA(cateId);
  }

  function switchToCMA() {
    setActiveGroup("CMA");
    setActiveCateId(null);
    loadCMA();
  }

  function switchToSCE() {
    setActiveGroup("SCE");
    setActiveCateId(null);
    loadSCE();
  }

  // --- 分頁頁碼 ---
  function getPageNumbersToShow() {
    const maxShow = 10;
    const start = Math.max(1, currentPage - Math.floor(maxShow / 2));
    const end = Math.min(totalPages, start + maxShow - 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  return (
    <>
      <div className="container">
        <div className="row mb-4">
          <div className="newtitle">考生心得分享</div>

          {/* 第一層：CPA / CMA / 學分班 */}
          <ul className="nav nav-tabs sharetab" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeGroup === "CPA" ? "active" : ""}`}
                type="button"
                onClick={() => {
                  // 若已選過 cateId 就重載該年；否則挑近五年的第一個
                  const target = activeCateId || cpaYears[0]?.cate_id;
                  if (target) switchToCPA(target);
                }}
              >
                CPA
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeGroup === "CMA" ? "active" : ""}`}
                type="button"
                onClick={switchToCMA}
              >
                CMA
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeGroup === "SCE" ? "active" : ""}`}
                type="button"
                onClick={switchToSCE}
              >
                學分班
              </button>
            </li>
          </ul>

          {/* CPA 的「近五年」按鈕 */}
          {activeGroup === "CPA" && cpaYears.length > 0 && (
            <ul className="nav nav-pills mt-3 flex-wrap" role="tablist">
              {cpaYears.map(({ year, cate_id }) => (
                <li className="nav-item mb-2" role="presentation" key={cate_id}>
                  <button
                    className={`nav-link ${
                      activeCateId === cate_id ? "active" : ""
                    }`}
                    type="button"
                    onClick={() => switchToCPA(cate_id)}
                  >
                    {year}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* 卡片清單 */}
      <div className="container">
        {loading && <div className="py-5 text-center">目前沒有資料</div>}
        {!loading && (
          <div className="tab-content row justify-content-center">
            {currentCards.map((item, index) => {
              const key = `${index}-${item.art_id || "k" + index}`;
              const cover =
                item.cover_1 && item.cover_1.length > 0
                  ? item.cover_1
                  : "/images/400.png";

              return (
                <div className="card sharecard m-2 mb-5" key={key}>
                  <div className="card-body">
                    <div className="mb-4">
                      <img src={cover} alt={item.title || ""} />
                    </div>
                    <h5 className="card-title mb-4"><strong>{item.title}</strong></h5>
                    <p
                      className="card-text"
                      dangerouslySetInnerHTML={{ __html: item.intro || "" }}
                    />
                    <Link to={`/share/detail/${item.art_id}`} className="btn btn-primary">
                      more
                    </Link>
                  </div>
                </div>
              );
            })}
            {!currentCards.length && (
              <div className="py-5 text-center">目前沒有資料</div>
            )}
          </div>
        )}
      </div>


      {/* 分頁 */}
      {!loading && totalPages > 1 && (
        <div className="pagination mb100">
          {getPageNumbersToShow().map((num) => (
            <button
              className={`page-link ${num === currentPage ? "active" : ""}`}
              key={num}
              onClick={() => setCurrentPage(num)}
            >
              {num}
            </button>
          ))}
        </div>
      )}
    </>
  );
}
